// src/services/RetentionService.js
// Manages data retention policies per data type.
// ADM-203: backend dual-write (Wave 27 demo-29) via /api/retention/state.

import storage from './StorageAdapter';
import { applyDualWriteMixin, makeDualWriteMixin } from './_dualWriteHelpers';
import retentionApi from '../api/retention';

const POLICIES_KEY = 'retention_policies';
const ARCHIVE_KEY = 'retention_archive';
const STATE_KEY_POLICIES = 'policies';
const STATE_KEY_ARCHIVE = 'archive';

const DATA_TYPES = {
  audit_logs: {
    storageKey: 'rms_audit_logs',
    dateField: 'timestamp',
    isArray: true,
  },
  validation_history: {
    storageKey: 'scheduled_validation_history',
    dateField: 'timestamp',
    isArray: true,
  },
};

const DEFAULT_RETENTION_YEARS = 5;

class RetentionService {
  _entityKey(baseKey) {
    if (baseKey === STATE_KEY_POLICIES) return POLICIES_KEY;
    if (baseKey === STATE_KEY_ARCHIVE) return ARCHIVE_KEY;
    return baseKey;
  }

  getDataTypes() {
    return Object.entries(DATA_TYPES).map(([id, meta]) => ({ id, ...meta }));
  }

  getPolicies() {
    const saved = storage.getJSON(POLICIES_KEY, {});
    const result = {};
    Object.keys(DATA_TYPES).forEach((id) => {
      result[id] = {
        retentionYears: DEFAULT_RETENTION_YEARS,
        enabled: true,
        ...saved[id],
      };
    });
    return result;
  }

  async savePolicy(dataTypeId, { retentionYears, enabled }) {
    if (!DATA_TYPES[dataTypeId]) {
      throw new Error(`Unknown data type: ${dataTypeId}`);
    }
    const policies = storage.getJSON(POLICIES_KEY, {});
    policies[dataTypeId] = {
      retentionYears: Number(retentionYears) || DEFAULT_RETENTION_YEARS,
      enabled: Boolean(enabled),
    };
    await this._dualWriteJson(STATE_KEY_POLICIES, policies);
  }

  async archiveForDataType(dataTypeId) {
    const meta = DATA_TYPES[dataTypeId];
    if (!meta) return { archived: 0, remaining: 0 };

    const policies = this.getPolicies();
    const policy = policies[dataTypeId];
    if (!policy.enabled) return { archived: 0, remaining: 0 };

    const cutoffMs = Date.now() - policy.retentionYears * 365 * 24 * 60 * 60 * 1000;

    const records = storage.getJSON(meta.storageKey, []);
    if (!Array.isArray(records)) return { archived: 0, remaining: 0 };

    const toArchive = [];
    const toKeep = [];

    records.forEach((record) => {
      const dateStr = record[meta.dateField];
      if (!dateStr) {
        toKeep.push(record);
        return;
      }
      const recordMs = new Date(dateStr).getTime();
      if (!isNaN(recordMs) && recordMs < cutoffMs) {
        toArchive.push(record);
      } else {
        toKeep.push(record);
      }
    });

    if (toArchive.length > 0) {
      const archive = storage.getJSON(ARCHIVE_KEY, []);
      const now = new Date().toISOString();
      toArchive.forEach((record) => {
        archive.push({
          id: `arch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          dataTypeId,
          storageKey: meta.storageKey,
          record,
          archivedAt: now,
        });
      });
      await this._dualWriteJson(STATE_KEY_ARCHIVE, archive);
      await storage.setJSON(meta.storageKey, toKeep);
    }

    return { archived: toArchive.length, remaining: toKeep.length };
  }

  async runAllArchives() {
    const results = [];
    for (const dataTypeId of Object.keys(DATA_TYPES)) {
      results.push({ dataTypeId, ...await this.archiveForDataType(dataTypeId) });
    }
    return results;
  }

  getArchivedRecords(dataTypeId, query = '') {
    const archive = storage.getJSON(ARCHIVE_KEY, []);
    let results = dataTypeId
      ? archive.filter((entry) => entry.dataTypeId === dataTypeId)
      : archive;

    if (query) {
      const q = query.toLowerCase();
      results = results.filter((entry) => {
        const str = JSON.stringify(entry.record).toLowerCase();
        return str.includes(q);
      });
    }

    return results.slice().sort(
      (a, b) => new Date(b.archivedAt) - new Date(a.archivedAt)
    );
  }

  getTotalArchivedCount() {
    return storage.getJSON(ARCHIVE_KEY, []).length;
  }

  async restoreRecord(archiveEntryId) {
    const archive = storage.getJSON(ARCHIVE_KEY, []);
    const idx = archive.findIndex((e) => e.id === archiveEntryId);
    if (idx === -1) return false;

    const entry = archive[idx];
    const meta = DATA_TYPES[entry.dataTypeId];
    if (meta) {
      const records = storage.getJSON(entry.storageKey, []);
      if (Array.isArray(records)) {
        records.push(entry.record);
        await storage.setJSON(entry.storageKey, records);
      }
    }

    archive.splice(idx, 1);
    await this._dualWriteJson(STATE_KEY_ARCHIVE, archive);
    return true;
  }

  async purgeArchive(dataTypeId) {
    const archive = storage.getJSON(ARCHIVE_KEY, []);
    const remaining = archive.filter((e) => e.dataTypeId !== dataTypeId);
    const deleted = archive.length - remaining.length;
    await this._dualWriteJson(STATE_KEY_ARCHIVE, remaining);
    return deleted;
  }

  notifyListeners() {}

  async initFromBackend() {
    return this._initFromBackend([STATE_KEY_POLICIES, STATE_KEY_ARCHIVE], []);
  }
}

applyDualWriteMixin(RetentionService, makeDualWriteMixin({
  storage,
  api: retentionApi,
  serviceName: 'RetentionService',
}));

const retentionService = new RetentionService();
export default retentionService;
